import { useState } from "react";

const sizes = [
  {
    id: "small",
    label: "Small",
    dims: "Up to 1.5m × 1.0m",
    sqm: "Up to 1.5m²",
    priceFrom: "£1,650",
    priceTo: "£2,400",
    pitch: "15° – 25°",
    glazing: "Double unit standard",
    bestFor: ["Boot rooms", "WCs & cloakrooms", "Hallways", "Small landings"],
    roomSizeIdeal: "Under 10m²",
    leadTime: "2–3 weeks",
    planningNeeded: false,
    popular: false,
    description: "Our compact small lanterns are perfect for transforming tight, dark spaces. Even a 1m × 1m lantern floods a hallway or cloakroom with dramatic natural light — often the biggest transformation per square metre of any size.",
    visW: 40,
    visH: 26,
  },
  {
    id: "medium",
    label: "Medium",
    dims: "Up to 2.5m × 1.5m",
    sqm: "Up to 3.75m²",
    priceFrom: "£2,200",
    priceTo: "£3,800",
    pitch: "20° – 30°",
    glazing: "Double unit, solar control option",
    bestFor: ["Kitchen extensions", "Dining areas", "Garden rooms", "Utility rooms"],
    roomSizeIdeal: "10m² – 25m²",
    leadTime: "2–4 weeks",
    planningNeeded: false,
    popular: true,
    description: "The UK's most popular roof lantern size. Ideally suited to the typical single-storey kitchen extension, a medium lantern sits beautifully over a kitchen-diner and significantly reduces reliance on artificial lighting throughout the day.",
    visW: 64,
    visH: 38,
  },
  {
    id: "large",
    label: "Large",
    dims: "Up to 4.0m × 2.0m",
    sqm: "Up to 8.0m²",
    priceFrom: "£3,800",
    priceTo: "£6,500",
    pitch: "25° – 35°",
    glazing: "Double or triple unit, solar control recommended",
    bestFor: ["Open-plan kitchens", "Orangeries", "Garden rooms", "Full extensions"],
    roomSizeIdeal: "25m² – 50m²",
    leadTime: "3–5 weeks",
    planningNeeded: false,
    popular: false,
    description: "A large lantern is a serious architectural statement. Whether crowning a new orangery or sitting over a wide open-plan extension, these units transform entire rooms into light-filled showpieces. Solar control glazing is strongly recommended at this size.",
    visW: 90,
    visH: 50,
  },
  {
    id: "xl",
    label: "XL Bespoke",
    dims: "Over 4.0m × 2.0m",
    sqm: "Any dimension",
    priceFrom: "£6,500",
    priceTo: "POA",
    pitch: "Any angle",
    glazing: "Triple unit recommended, all options available",
    bestFor: ["Grand orangeries", "Commercial spaces", "Architects\u2019 projects", "Prestige homes"],
    roomSizeIdeal: "50m²+",
    leadTime: "4–8 weeks",
    planningNeeded: true,
    popular: false,
    description: "No dimension is too large for our bespoke manufacturing partners. Prestige projects, architect-designed homes, and commercial spaces all benefit from our ability to manufacture and install truly custom roof lanterns with no standard upper size limit.",
    visW: 110,
    visH: 62,
  },
];

const pitchVisuals = [
  { angle: "15°", label: "Low Pitch", desc: "Sleek, minimal profile. Suits contemporary flat-roof extensions perfectly. Maximum external discretion.", colour: "bg-zinc-700" },
  { angle: "25°", label: "Standard Pitch", desc: "The most common pitch. Excellent balance of internal height, external aesthetics, and drainage performance.", colour: "bg-amber-600" },
  { angle: "35°", label: "Steep Pitch", desc: "Creates impressive internal height. Best for large rooms and orangeries where drama and volume are desired.", colour: "bg-zinc-800" },
];

const colourOptions = [
  { name: "White", ral: "RAL 9010", hex: "#F5F5F0", textDark: true },
  { name: "Cream", ral: "RAL 9001", hex: "#F3EED9", textDark: true },
  { name: "Anthracite Grey", ral: "RAL 7016", hex: "#393F43", textDark: false },
  { name: "Black", ral: "RAL 9005", hex: "#0E0E10", textDark: false },
  { name: "Sage Green", ral: "RAL 6021", hex: "#87A47C", textDark: false },
  { name: "Slate Blue", ral: "RAL 5014", hex: "#627896", textDark: false },
  { name: "Heritage Cream", ral: "RAL 9002", hex: "#E7E0D0", textDark: true },
  { name: "Any RAL Colour", ral: "Bespoke", hex: "linear-gradient(135deg, #E57373 0%, #81C784 33%, #64B5F6 66%, #FFD54F 100%)", textDark: true },
];

export default function SizingGuide() {
  const [activeSize, setActiveSize] = useState("medium");
  const selected = sizes.find(s => s.id === activeSize) ?? sizes[1];

  return (
    <section id="sizing-guide" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Sizing Guide</span>
          <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Which Roof Lantern Size is Right for You?
          </h2>
          <p className="text-zinc-500 text-sm mt-3 leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Every roof lantern we supply is made to your exact opening dimensions. Use this guide to understand what size suits your room, budget, and project goals.
          </p>
        </div>

        {/* Size Selector Tabs */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {sizes.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSize(s.id)}
              className={`relative px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer border-2 ${
                activeSize === s.id
                  ? "bg-amber-600 border-amber-600 text-white"
                  : "bg-white border-stone-200 text-zinc-600 hover:border-amber-400 hover:text-amber-600"
              }`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {s.label}
              {s.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  Most Popular
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Selected Size Detail */}
        <div className="bg-stone-50 rounded-xl border border-stone-100 overflow-hidden mb-14">
          <div className="grid lg:grid-cols-2">
            {/* Visual / diagram side */}
            <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 p-10 flex flex-col items-center justify-center min-h-[320px] relative overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px)" }}></div>
              </div>
              <div className="relative z-10 flex flex-col items-center">
                {/* Lantern shape diagram */}
                <div className="relative flex flex-col items-center mb-6">
                  {/* Roof (triangle) */}
                  <div
                    className="relative bg-amber-500/80"
                    style={{
                      width: `${selected.visW * 1.6}px`,
                      height: `${selected.visH * 0.6}px`,
                      clipPath: "polygon(10% 100%, 90% 100%, 50% 0%)",
                    }}
                  ></div>
                  {/* Body */}
                  <div
                    className="bg-amber-400/30 border-2 border-amber-400/60"
                    style={{
                      width: `${selected.visW * 1.4}px`,
                      height: `${selected.visH * 0.4}px`,
                    }}
                  >
                    <div className="w-full h-full grid grid-cols-3 gap-0.5 p-0.5">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-sky-300/20 border border-sky-300/30 rounded-sm"></div>
                      ))}
                    </div>
                  </div>
                  {/* Base */}
                  <div
                    className="bg-zinc-600 rounded-sm"
                    style={{
                      width: `${selected.visW * 1.5}px`,
                      height: "8px",
                    }}
                  ></div>
                </div>

                {/* Dimension labels */}
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-amber-400 text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {selected.dims.split("×")[0].trim()}
                    </p>
                    <p className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>length</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center text-stone-500">
                    <i className="ri-close-line"></i>
                  </div>
                  <div>
                    <p className="text-amber-400 text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {selected.id === "xl" ? "Custom" : selected.dims.split("×")[1]?.trim()}
                    </p>
                    <p className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>width</p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <span className="text-stone-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>Pitch: <strong className="text-white">{selected.pitch}</strong></span>
                  <span className="text-stone-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>Roof area: <strong className="text-white">{selected.sqm}</strong></span>
                </div>
              </div>
            </div>

            {/* Info side */}
            <div className="p-8 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selected.label} Roof Lantern</h3>
                  <p className="text-zinc-400 text-sm mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{selected.dims}</p>
                </div>
                <div className="text-right">
                  <p className="text-amber-600 text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selected.priceFrom}</p>
                  <p className="text-zinc-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    {selected.priceTo === "POA" ? "upwards — POA for XL" : `– ${selected.priceTo} fully installed`}
                  </p>
                </div>
              </div>

              <p className="text-zinc-500 text-sm leading-relaxed mb-5 border-l-2 border-amber-200 pl-4" style={{ fontFamily: "'Raleway', sans-serif" }}>
                {selected.description}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Ideal Room Size</p>
                  <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{selected.roomSizeIdeal}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Lead Time</p>
                  <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{selected.leadTime}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Standard Glazing</p>
                  <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{selected.glazing}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Planning Required</p>
                  <p className={`text-sm font-semibold ${selected.planningNeeded ? "text-amber-600" : "text-emerald-600"}`} style={{ fontFamily: "'Raleway', sans-serif" }}>
                    {selected.planningNeeded ? "Typically Yes — check required" : "Usually Not Required"}
                  </p>
                </div>
              </div>

              <div className="mb-5">
                <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Best For</p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.bestFor.map(tag => (
                    <span key={tag} className="bg-amber-50 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full border border-amber-100" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <a
                href="#quote-form"
                className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold py-3 px-6 rounded-md transition-colors whitespace-nowrap cursor-pointer mt-auto"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Get a Quote for {selected.label}
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
              </a>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-14">
          <h3 className="text-2xl font-bold text-zinc-900 mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>All Sizes at a Glance</h3>
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="w-full text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <thead>
                <tr className="bg-zinc-900 text-left">
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide">Size</th>
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide">Dimensions</th>
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide">Price Guide</th>
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide">Lead Time</th>
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide hidden md:table-cell">Best Room Size</th>
                  <th className="px-5 py-4 text-stone-300 font-semibold text-xs uppercase tracking-wide hidden lg:table-cell">Planning</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map((s, i) => (
                  <tr
                    key={s.id}
                    onClick={() => setActiveSize(s.id)}
                    className={`border-t border-stone-100 cursor-pointer transition-colors ${
                      activeSize === s.id ? "bg-amber-50" : i % 2 === 0 ? "bg-white hover:bg-stone-50" : "bg-stone-50/50 hover:bg-stone-50"
                    }`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-800">{s.label}</span>
                        {s.popular && (
                          <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">Popular</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-zinc-600">{s.dims}</td>
                    <td className="px-5 py-4">
                      <span className="text-amber-700 font-semibold">{s.priceFrom}</span>
                      {s.priceTo !== "POA" && <span className="text-zinc-400"> – {s.priceTo}</span>}
                      {s.priceTo === "POA" && <span className="text-zinc-400"> upwards</span>}
                    </td>
                    <td className="px-5 py-4 text-zinc-600">{s.leadTime}</td>
                    <td className="px-5 py-4 text-zinc-600 hidden md:table-cell">{s.roomSizeIdeal}</td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className={`text-xs font-semibold ${s.planningNeeded ? "text-amber-600" : "text-emerald-600"}`}>
                        {s.planningNeeded ? "Usually Required" : "Usually Exempt"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-400 mt-3 text-center" style={{ fontFamily: "'Raleway', sans-serif" }}>
            * Prices are fully installed including VAT and labour. Actual price depends on structural opening, access, and specification. Contact us for a written quote.
          </p>
        </div>

        {/* Pitch Guide */}
        <div className="mb-14">
          <h3 className="text-2xl font-bold text-zinc-900 mb-2 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Pitch Angle Guide</h3>
          <p className="text-zinc-500 text-sm text-center mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
            The pitch angle of your roof lantern affects both the internal volume and the external profile. We recommend angles based on your room size and aesthetic preference.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {pitchVisuals.map(p => (
              <div key={p.angle} className="bg-stone-50 border border-stone-100 rounded-lg p-6 flex flex-col items-center text-center">
                {/* Visual pitch shape */}
                <div className="relative w-24 h-16 mb-4">
                  <div className="absolute bottom-0 left-0 right-0 h-2 bg-zinc-400 rounded-sm"></div>
                  <div
                    className={`absolute bottom-2 left-1/2 -translate-x-1/2 ${p.colour}`}
                    style={{
                      width: "80px",
                      height: p.angle === "15°" ? "14px" : p.angle === "25°" ? "24px" : "38px",
                      clipPath: "polygon(10% 100%, 90% 100%, 50% 0%)",
                    }}
                  ></div>
                </div>
                <span className="text-3xl font-bold text-zinc-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.angle}</span>
                <span className="text-amber-600 text-xs font-bold tracking-wide uppercase mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{p.label}</span>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Colour Options */}
        <div>
          <h3 className="text-2xl font-bold text-zinc-900 mb-2 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Frame Colour Options</h3>
          <p className="text-zinc-500 text-sm text-center mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Available in any RAL colour. Our most popular finishes are shown below. Bespoke RAL colours are available at no extra charge.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {colourOptions.map(c => (
              <div key={c.name} className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-lg px-4 py-3">
                <div
                  className="w-7 h-7 rounded-md flex-shrink-0 border border-stone-200"
                  style={{
                    background: c.hex.startsWith("linear") ? c.hex : c.hex,
                  }}
                ></div>
                <div>
                  <p className="text-zinc-800 text-xs font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{c.name}</p>
                  <p className="text-zinc-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{c.ral}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
